import {
  Fragment,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { onSnapshot } from "firebase/firestore";
import { Link } from "react-router-dom";
import { saveAs } from "file-saver";
import "../../../../css/Finance/CommissionStatementPDF.css";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import SearchField from "../../../DesignSystem/Inputs/SearchField";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import IconButton from "../../../DesignSystem/Buttons/IconButton";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import ButtonTextLink from "../../../DesignSystem/Buttons/ButtonTextLink";
import Popup from "../../../DesignSystem/Popups/Popup";
import Field from "../../../DesignSystem/Inputs/Field";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import { useUserEmail } from "../../../SignIn/UserEmailContext";
import {
  getFCIStatementsPage,
  getIOIntroducers,
} from "../../FCIStatements/fciStatementsService";
import { getIODetailsRef } from "../../../Adviser/Tabs/IODetails/ioDetailsService";
import { loadAMLClientName } from "../../AMLStatements.js/amlDashboard";
import { subscribeSuspenseStatement } from "../services/suspenseStatementService";
import { normaliseSuspenseRows } from "../utils/suspenseStatementParser";
import {
  saveQuilterAdviserLinks,
  subscribeQuilterStatement,
} from "../services/quilterStatementService";
import { normaliseQuilterRows } from "../utils/quilterStatementParser";
import { subscribeFinplanStatement } from "../services/finplanStatementService";
import { finplanCommissionRows } from "../utils/finplanAdviserLinks";
import { useAdviserCharges } from "../context/AdviserChargesContext";
import { subscribeMonthlyManualPayments } from "../services/manualPaymentService";
import { calculateAccountBalances } from "../utils/accountBalances";
import { formatMonthKey } from "../utils/chargeDates";
import { paymentsForMonth } from "../utils/paymentDates";
import {
  getIncomePeriodStartMonthKey,
  monthKeyFromDate,
} from "../utils/incomePeriod";
import { groupUnmatchedIoPayments } from "../utils/unmatchedIoUsers";
import {
  adviserIoDetailsDocumentId,
  uniqueIoRecipientIdsByEmail,
} from "../utils/ioRecipientMapping";
import {
  getSavedGroupRecords,
  resolveSavedGroupLogoUrl,
} from "../../../GroupDetails/groupDetailsService";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import { ReactComponent as ViewIcon } from "../../../../assets/icons/view.svg";
import { ReactComponent as PasswordKeyIcon } from "../../../../assets/icons/PasswordKey.svg";
import { ReactComponent as DownloadIcon } from "../../../../assets/icons/download.svg";
import { ReactComponent as ArrowDownIcon } from "../../../../assets/icons/arrow-down.svg";
import { ReactComponent as CircleCheckIcon } from "../../../../assets/icons/Circle Check (filled).svg";
import { ReactComponent as CircleErrorIcon } from "../../../../assets/icons/Circle Error (filled).svg";
import { ReactComponent as WarningIcon } from "../../../../assets/icons/warning-yellow.svg";
import { ReactComponent as SendMailIcon } from "../../../../assets/icons/finance/send-mail.svg";
import { ReactComponent as RegenerateIcon } from "../../../../assets/icons/finance/regenerate.svg";
import { ReactComponent as EditIcon } from "../../../../assets/icons/finance/Edit.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/paraplanningDashboard/Cross.svg";
import {
  buildFeesAndChargesBreakdown,
  mergeFeesAndChargesBreakdowns,
} from "../components/CommissionStatementPDF/utils/feesAndChargesBreakdown";
import { buildCommissionBreakdown } from "../components/CommissionStatementPDF/utils/commissionBreakdown";
import {
  createMonthlyStatementDownloadArchive,
  createMonthlyStatementSendBatch,
  generateFreshMonthlyStatementFile,
  generateFreshMonthlyStatementVersion,
  getMonthlyStatementHistory,
  getMonthlyStatementSummaries,
  getStoredMonthlyStatementFile,
  subscribeMonthlyStatementHistory,
  subscribeMonthlyStatementDownloadArchive,
  subscribeMonthlyStatementSendBatch,
  subscribeMonthlyStatementSendBatchItems,
  subscribeMonthlyStatementSummaries,
} from "../services/monthlyStatementArchiveService";
import {
  saveIORecipientMapping,
  saveIORecipientMappings,
  saveManualPaymentRecipientMapping,
  separateIORecipientMapping,
  subscribeIORecipientMappings,
} from "../services/ioRecipientMappingService";
import {
  ioIntroducerTargets,
  mappedIoIntroducerGroups,
  mappedRecipientIdsByAdviser,
} from "../utils/ioRecipientTargets";
import {
  isStatementSendEligible,
  statementActionAvailability,
  statementClosingBalance,
} from "../utils/commissionStatementEligibility";
import { buildPaymentSummary } from "../utils/paymentSummary";
import {
  manualPaymentFeeEntries,
  manualPaymentFeeEntry,
} from "../utils/manualPaymentCharges";
import {
  commissionStatementFileName,
  personalAdviserName,
} from "../utils/commissionStatementFileName";
import { linkQuilterSourcesToAdviser } from "../utils/quilterAdviserLinks";
import {
  groupStatementVariantForAdviser,
  groupStatementVariants,
  hasGroupStatementVariants,
  isExcludedGroupStatementAdviser,
} from "../utils/groupStatementExceptions";
import { statementPasswordFromDob } from "../utils/statementPassword";
import { resolveGroupStatementPassword } from "../utils/groupStatementPassword";
import {
  normaliseStatementCc,
  resolveStatementRecipientEmails,
  statementCcEmails,
  statementRecipientGroupIds,
} from "../../../Adviser/Tabs/Rates/statementRecipients";
import {
  saveStatementPassword,
  statementPasswordKey,
  subscribeStatementPasswords,
} from "../services/statementPasswordService";


const keyOf = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const DEFAULT_STATEMENT_EMAIL = "accounts@rosemountfs.co.uk";
const queueValue = (value) => {
  if (value === null || value === undefined) return null;
  if (["string", "number", "boolean"].includes(typeof value)) return value;
  if (typeof value?.toDate === "function") return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(queueValue);
  if (typeof value === "object") return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => typeof item !== "function" && item !== undefined)
      .map(([name, item]) => [name, queueValue(item)]),
  );
  return String(value);
};
const ENTRY_TYPE_OPTIONS = [
  { id: "adviser", name: "Advisers" },
  { id: "group", name: "Groups" },
  { id: "unmatched-quilter", name: "Unmatched Quilter Advisers" },
  { id: "io-introducer", name: "IO Introducers" },
  { id: "unknown", name: "Unknown" },
  { id: "unmatched-io", name: "Unmatched IO Users" },
];
const EXTERNAL_STATEMENT_TYPES = new Set([
  "unmatched-quilter",
  "io-introducer",
  "unknown",
  "unmatched-io",
]);
const money = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value || 0));
const PAYMENT_SUMMARY_ITEMS = [
  ["openingBalance", "Opening Balance"],
  ["totalCommission", "Total Commission"],
  ["totalCharged", "Total Charged"],
  ["payments", "Payments", true],
  ["advances", "Advances", true],
  ["balanceToPay", "Closing Balance"],
];
const PaymentSummaryCell = ({ summary }) => (
  <div className="commission-payment-summary">
    {PAYMENT_SUMMARY_ITEMS.filter(
      ([key, , nonZeroOnly]) => !nonZeroOnly || Number(summary?.[key] || 0) !== 0,
    ).map(([key, label]) => {
      const amount = Number(summary?.[key] || 0);
      return (
        <div
          className={`commission-payment-summary-row ${key === "balanceToPay" ? "commission-payment-summary-total" : ""}`}
          key={key}
        >
          <span>{label}</span>
          <span className={amount < 0 ? "text-error" : ""}>{money(amount)}</span>
        </div>
      );
    })}
  </div>
);
const statementStatusIcon = (status) => {
  const key = String(status || "").trim().toLowerCase();
  if (key === "sent") return <CircleCheckIcon aria-hidden="true" />;
  if (key === "failed") return <CircleErrorIcon aria-hidden="true" />;
  return null;
};
const hasMissingStatementPassword = (row = {}) => {
  if (row.entryType === "group") return false;
  if (String(row.statementPassword || "").replace(/\s+/g, "")) {
    return false;
  }
  if (row.entryType === "adviser") {
    return !statementPasswordFromDob(row.adviserDob);
  }
  return true;
};
const CommissionStatements = ({
  selectedMonth,
  monthOptions,
  onMonthChange,
}) => {
  const { advisers, loadingAdvisers } = useAdviserList();
  const {
    advisers: unappointedAdvisers,
    loadingAdvisers: loadingUnappointedAdvisers,
  } = useAdviserListWithUnappointed();
  const { userEmail } = useUserEmail();
  const {
    loadedChargeRecords,
    chargeRecords,
    advisersByEmail,
    chargesLoading,
    incomePeriods,
  } = useAdviserCharges();
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [selected, setSelected] = useState(new Set());
  const [sort, setSort] = useState({ key: "adviser", direction: 1 });
  const [groupAccounts, setGroupAccounts] = useState({});
  const [entryType, setEntryType] = useState("adviser");
  const [previewRow, setPreviewRow] = useState(null);
  const [ioIdsByEmail, setIoIdsByEmail] = useState({});
  const [ioIdsLoading, setIoIdsLoading] = useState(true);
  const [suspenseStatement, setSuspenseStatement] = useState(null);
  const [quilterStatement, setQuilterStatement] = useState(null);
  const [quilterLoading, setQuilterLoading] = useState(true);
  const [finplanStatement, setFinplanStatement] = useState(null);
  const [finplanLoading, setFinplanLoading] = useState(true);
  const [ioStatementRows, setIoStatementRows] = useState([]);
  const [unmatchedIoLoading, setUnmatchedIoLoading] = useState(false);
  const [ioRecipientMappings, setIoRecipientMappings] = useState({});
  const [ioMappingsLoading, setIoMappingsLoading] = useState(true);
  const [ioIntroducers, setIoIntroducers] = useState([]);
  const [ioIntroducersLoading, setIoIntroducersLoading] = useState(true);
  const [mappingRow, setMappingRow] = useState(null);
  const [mappingPosition, setMappingPosition] = useState(null);
  const [mappingValue, setMappingValue] = useState("");
  const [mappingSaving, setMappingSaving] = useState(false);
  const [mappingError, setMappingError] = useState("");
  const [statementPasswords, setStatementPasswords] = useState({});
  const [passwordRow, setPasswordRow] = useState(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [amlClientNames, setAmlClientNames] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const [sendingStatements, setSendingStatements] = useState(false);
  const [sendConfirmationOpen, setSendConfirmationOpen] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  const [sendJobItems, setSendJobItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [statementSummaries, setStatementSummaries] = useState(new Map());
  const [storedPreviewUrl, setStoredPreviewUrl] = useState("");
  const [expandedHistoryKey, setExpandedHistoryKey] = useState("");
  const [historyVersionsByKey, setHistoryVersionsByKey] = useState({});
  const [collapsedHistoryVersions, setCollapsedHistoryVersions] = useState(
    () => new Set(),
  );
  const historySubscriptionsRef = useRef(new Map());
  const sendJobSubscriptionsRef = useRef([]);
  const downloadJobSubscriptionRef = useRef(null);
  const downloadedJobIdRef = useRef("");

  const statementIncomePeriodId = selectedMonth;
  const applyStatementSummaries = useCallback((summaries) => {
    setStatementSummaries(
      new Map(
        summaries.map((summary) => [keyOf(summary.adviserKey), summary]),
      ),
    );
  }, []);
  const refreshStatementSummaries = useCallback(async () => {
    try {
      const summaries = await getMonthlyStatementSummaries(
        statementIncomePeriodId,
      );
      applyStatementSummaries(summaries);
    } catch {
      setStatementSummaries(new Map());
    }
  }, [applyStatementSummaries, statementIncomePeriodId]);
  useEffect(() => {
    const unsubscribe = subscribeMonthlyStatementSummaries(
      statementIncomePeriodId,
      applyStatementSummaries,
      refreshStatementSummaries,
    );
    return unsubscribe;
  }, [applyStatementSummaries, refreshStatementSummaries, statementIncomePeriodId]);
  useEffect(() => () => {
    historySubscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
    historySubscriptionsRef.current.clear();
  }, [statementIncomePeriodId]);
  useEffect(() => () => {
    sendJobSubscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
    sendJobSubscriptionsRef.current = [];
  }, []);
  useEffect(() => () => {
    downloadJobSubscriptionRef.current?.();
    downloadJobSubscriptionRef.current = null;
  }, []);
  useEffect(
    () => () => {
      if (storedPreviewUrl) URL.revokeObjectURL(storedPreviewUrl);
    },
    [storedPreviewUrl],
  );
  useEffect(
    () => subscribeMonthlyManualPayments(setPayments, () => setPayments([])),
    [],
  );
  useEffect(
    () => subscribeStatementPasswords(
      setStatementPasswords,
      () => setStatementPasswords({}),
    ),
    [],
  );
  useEffect(
    () =>
      subscribeSuspenseStatement(selectedMonth, setSuspenseStatement, () =>
        setSuspenseStatement(null),
      ),
    [selectedMonth],
  );
  useEffect(() => {
    setQuilterLoading(true);
    setQuilterStatement(null);
    return subscribeQuilterStatement(
      selectedMonth,
      (statement) => {
        setQuilterStatement(statement);
        setQuilterLoading(false);
      },
      () => {
        setQuilterStatement(null);
        setQuilterLoading(false);
      },
    );
  }, [selectedMonth]);
  useEffect(() => {
    setFinplanLoading(true);
    setFinplanStatement(null);
    return subscribeFinplanStatement(
      selectedMonth,
      (statement) => {
        setFinplanStatement(statement);
        setFinplanLoading(false);
      },
      () => {
        setFinplanStatement(null);
        setFinplanLoading(false);
      },
    );
  }, [selectedMonth]);
  useEffect(() => {
    setSelected(new Set());
    setPreviewRow(null);
  }, [selectedMonth]);

  useEffect(() => {
    let active = true;
    const amlChecks = chargeRecords.filter(
      (row) => row.chargeType === "AML Checks" && row.__ref,
    );
    if (!amlChecks.length)
      return () => {
        active = false;
      };

    Promise.all(
      amlChecks.map(async (row) => [
        row.id,
        (await loadAMLClientName(row)) || row.client || "",
      ]),
    ).then((entries) => {
      if (active) {
        setAmlClientNames((current) => ({
          ...current,
          ...Object.fromEntries(entries),
        }));
      }
    });
    return () => {
      active = false;
    };
  }, [chargeRecords]);

  useEffect(() => {
    let active = true;
    getSavedGroupRecords()
      .then((groups) =>
        Promise.all(
          groups.map(async (group) => ({
            ...group,
            logoUrl: await resolveSavedGroupLogoUrl(group),
          })),
        ),
      )
      .then((groups) => {
        if (!active) return;
        setGroupAccounts(
          Object.fromEntries(
            groups.map((group) => [String(group.ioGroupId || group.id), group]),
          ),
        );
      })
      .catch(() => {
        if (active) setGroupAccounts({});
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setIoIdsLoading(true);
    if (!advisers.length) {
      setIoIdsByEmail({});
      setIoIdsLoading(false);
      return () => {
        active = false;
      };
    }

    const entries = new Array(advisers.length).fill(null);
    const settled = new Set();
    const publish = (index, value) => {
      if (!active) return;
      entries[index] = value;
      settled.add(index);
      setIoIdsByEmail(
        uniqueIoRecipientIdsByEmail(entries.filter(Boolean)),
      );
      if (settled.size === advisers.length) setIoIdsLoading(false);
    };
    const unsubscribes = advisers.map((adviser, index) => {
      const email = keyOf(adviser.email || adviser.id);
      if (!email) {
        publish(index, null);
        return () => {};
      }
      return onSnapshot(
        getIODetailsRef(adviserIoDetailsDocumentId(adviser)),
        (snapshot) => publish(index, {
          email,
          isAppointed: adviser.isAppointed,
          adviser,
          ioDetails: snapshot.exists() ? snapshot.data() : {},
        }),
        () => publish(index, null),
      );
    });
    return () => {
      active = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [advisers]);

  useEffect(() => {
    const unsubscribe = subscribeIORecipientMappings(
      (mappings) => {
        setIoRecipientMappings(mappings);
        setIoMappingsLoading(false);
      },
      () => {
        setIoRecipientMappings({});
        setIoMappingsLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    let active = true;
    setIoIntroducersLoading(true);
    getIOIntroducers(userEmail)
      .then((rows) => {
        if (active) setIoIntroducers(rows);
      })
      .catch(() => {
        if (active) setIoIntroducers([]);
      })
      .finally(() => {
        if (active) setIoIntroducersLoading(false);
      });
    return () => { active = false; };
  }, [userEmail]);

  useEffect(() => {
    let active = true;
    const latestClosed = incomePeriods?.[0] || {};
    const currentMonth = monthKeyFromDate(
      latestClosed.endDatetime ||
        latestClosed.endDate ||
        latestClosed.incomePeriodEndDatetime,
    );
    const matchingPeriod = (incomePeriods || []).find(
      (period) => getIncomePeriodStartMonthKey(period) === selectedMonth,
    );
    const period = selectedMonth === currentMonth
      ? "current"
      : String(matchingPeriod?.incomePeriodId || "");
    if (!period) {
      setIoStatementRows([]);
      setUnmatchedIoLoading(false);
      return () => { active = false; };
    }

    setUnmatchedIoLoading(true);
    setIoStatementRows([]);
    getFCIStatementsPage(1, 100000, userEmail, {
      _period: period,
      _sort: "PaymentCreated",
      _direction: "desc",
      _export: "true",
    })
      .then((result) => {
        if (active) setIoStatementRows(result.rows || []);
      })
      .catch(() => {
        if (active) setIoStatementRows([]);
      })
      .finally(() => {
        if (active) setUnmatchedIoLoading(false);
      });
    return () => { active = false; };
  }, [incomePeriods, selectedMonth, userEmail]);

  const suspenseRows = useMemo(() => {
    const links = new Map(
      (suspenseStatement?.adviserLinks || []).map((link) => [
        keyOf(link.sourceAdviserName),
        keyOf(link.email),
      ]),
    );
    const advisersByName = new Map(
      advisers.map((adviser) => [
        keyOf(adviser.name),
        keyOf(adviser.email || adviser.id),
      ]),
    );
    return normaliseSuspenseRows(suspenseStatement?.rows || []).map((row) => ({
      ...row,
      adviserEmail:
        links.get(keyOf(row.SourceAdviserName)) ||
        advisersByName.get(keyOf(row.MappedAdviserName)) ||
        "",
    }));
  }, [advisers, suspenseStatement]);

  const mappedIdsByAdviser = useMemo(
    () => mappedRecipientIdsByAdviser(ioRecipientMappings),
    [ioRecipientMappings],
  );

  const commissionSources = useMemo(() => {
    const ioByRecipientId = new Map();
    ioStatementRows.forEach((row) => {
      const recipientId = String(
        row.RecipientCrmId ??
          row.RecipientUserId ??
          row.RecipientId ??
          row.AdviserId ??
          "",
      ).trim();
      if (!recipientId) return;
      const entries = ioByRecipientId.get(recipientId) || [];
      entries.push(row);
      ioByRecipientId.set(recipientId, entries);
    });

    const quilterLinks = new Map(
      (quilterStatement?.adviserLinks || []).map((link) => [
        keyOf(link.sourceAdviserName),
        keyOf(link.email),
      ]),
    );
    const adviserDirectory = new Map(
      advisers.map((adviser) => [
        keyOf(adviser.name),
        keyOf(adviser.email || adviser.id),
      ]),
    );
    const quilterRows = normaliseQuilterRows(quilterStatement?.rows || []).map(
      (row) => {
        const sourceName = keyOf(
          row.SourceAdviserName || row.MappedAdviserName,
        );
        return {
          row,
          sourceName,
          mappedName: keyOf(row.MappedAdviserName),
          linkedEmail:
            quilterLinks.get(sourceName) ||
            adviserDirectory.get(keyOf(row.MappedAdviserName)) ||
            "",
        };
      },
    );
    const finplanRows = finplanCommissionRows({
      statement: finplanStatement,
      advisers,
      statementMonth: selectedMonth,
    }).map((row) => ({
      row,
      linkedEmail: keyOf(row.RecipientEmail),
      recipientName: keyOf(row.Recipient),
    }));
    return { ioByRecipientId, quilterRows, finplanRows };
  }, [advisers, finplanStatement, ioStatementRows, quilterStatement, selectedMonth]);

  const introducerTargets = useMemo(
    () => ioIntroducerTargets(ioIntroducers.length ? ioIntroducers : ioStatementRows),
    [ioIntroducers, ioStatementRows],
  );

  const mappingAdvisers = useMemo(() => {
    const byEmail = new Map();
    [...advisers, ...unappointedAdvisers].forEach((adviser) => {
      const email = keyOf(adviser.email || adviser.id);
      if (!email) return;
      byEmail.set(email, { ...byEmail.get(email), ...adviser });
    });
    return [...byEmail.values()];
  }, [advisers, unappointedAdvisers]);

  const mappingOptions = useMemo(() => [
    ...mappingAdvisers
      .map((adviser) => {
        const email = keyOf(adviser.email || adviser.id);
        if (!email) return null;
        const name = adviser.name || adviser.email || adviser.id;
        return {
          id: `adviser:${email}`,
          name: `[Adviser${adviser.isUnappointed ? " · Unappointed" : ""}] ${name} - ${email}`,
          target: {
            type: "adviser",
            targetId: email,
            email,
            name,
            adviser,
          },
        };
      })
      .filter(Boolean),
    ...introducerTargets.map((introducer) => ({
      id: introducer.id,
      name: introducer.optionName,
      target: introducer,
    })),
  ].sort((left, right) => left.name.localeCompare(right.name, "en-GB", {
    numeric: true,
  })), [introducerTargets, mappingAdvisers]);

  const recipientMappingOptions = useMemo(
    () => ["io-introducer", "unmatched-quilter"].includes(mappingRow?.entryType)
      ? mappingOptions.filter((option) => option.target?.type === "adviser")
      : mappingOptions,
    [mappingOptions, mappingRow],
  );

  const allAdviserRows = useMemo(() => {
    const { balances, currentMovements } = calculateAccountBalances({
      records: loadedChargeRecords,
      payments,
      month: selectedMonth,
    });
    const listedByEmail = new Map(
      mappingAdvisers.map((adviser) => [keyOf(adviser.email || adviser.id), adviser]),
    );
    const rateRowsByEmail = new Map(
      Object.entries(advisersByEmail).map(([email, value]) => [
        keyOf(email),
        value.adviser || {},
      ]),
    );
    const emails = new Set(
      [
        ...listedByEmail.keys(),
        ...balances.keys(),
        ...chargeRecords.map((record) => keyOf(record.adviserEmail)),
      ].filter(Boolean),
    );
    const downloadFolderByAdviserEmail = new Map();
    Object.values(groupAccounts).forEach((group) => {
      const linkedEmails = [
        ...new Set(
          [
            ...(Array.isArray(group.adviserEmails)
              ? group.adviserEmails
              : []),
            ...(Array.isArray(group.adviserLinks)
              ? group.adviserLinks.map((link) => link.adviserEmail)
              : []),
          ]
            .map(keyOf)
            .filter(Boolean),
        ),
      ];
      const groupId = String(group.ioGroupId || group.id || "");
      const groupName =
        group.groupName || group.group?.GroupName || `Group ${groupId}`;
      linkedEmails.forEach((linkedEmail) => {
        const linkedAdviser = listedByEmail.get(linkedEmail);
        const variant = groupStatementVariantForAdviser(groupId, {
          adviserDocumentId: linkedAdviser?.adviserId,
          email: linkedEmail,
        });
        const folderName = linkedEmails.length > 1
          ? variant?.name || (
              hasGroupStatementVariants(groupId) ? "No Practice" : groupName
            )
          : "No Practice";
        downloadFolderByAdviserEmail.set(linkedEmail, folderName);
      });
    });
    return [...emails].map((email) => {
      const listed = listedByEmail.get(email);
      const details = advisersByEmail[email]?.adviser;
      const paymentRecipient = String(
        details?.sendCommissionPaymentsTo ||
          details?.rateData?.sendCommissionPaymentsTo ||
          DEFAULT_STATEMENT_EMAIL,
      ).trim();
      const groupId = paymentRecipient.startsWith("group:")
        ? paymentRecipient.slice("group:".length)
        : "";
      const groupRecord = groupId ? groupAccounts[groupId] : null;
      const recipientEmail = groupId ? "" : keyOf(paymentRecipient || email);
      const recipientRates =
        rateRowsByEmail.get(recipientEmail) || details || {};
      const recipientAdviser = listedByEmail.get(recipientEmail);
      const accountHolder = groupId
        ? groupRecord?.bankDetails?.arAccountHolder
        : recipientRates.adviserAccountHolder || recipientRates.arAccountHolder;
      const accountNumber = groupId
        ? groupRecord?.bankDetails?.arAccountNumber
        : recipientRates.adviserAccountNumber || recipientRates.arAccountNumber;
      const recipientName = groupId
        ? groupRecord?.groupName ||
          groupRecord?.group?.GroupName ||
          `Group ${groupId}`
        : recipientEmail === DEFAULT_STATEMENT_EMAIL
          ? "Accounts"
          : recipientAdviser?.name || recipientRates.adviser || recipientEmail;
      const statementRecipientValue =
        details?.emailCommissionStatementsTo ||
        details?.rateData?.emailCommissionStatementsTo ||
        DEFAULT_STATEMENT_EMAIL;
      const statementEmail = resolveStatementRecipientEmails(
        statementRecipientValue,
        groupAccounts,
      ) || DEFAULT_STATEMENT_EMAIL;
      const inheritedGroupCc = statementRecipientGroupIds(
        statementRecipientValue,
      ).flatMap((selectedGroupId) =>
        statementCcEmails(
          groupAccounts[selectedGroupId]?.bankDetails
            ?.emailCommissionStatementsCc,
        ),
      );
      const statementCc = normaliseStatementCc([
        ...statementCcEmails(
          details?.emailCommissionStatementsCc ||
            details?.rateData?.emailCommissionStatementsCc ||
            "",
        ),
        ...inheritedGroupCc,
      ]);
      const statementPassword = String(
        details?.statementPassword ||
          details?.rateData?.statementPassword ||
          "",
      ).replace(/\s+/g, "");
      const adviserChargeEntries = chargeRecords
        .filter((charge) => keyOf(charge.adviserEmail) === email)
        .map((charge) =>
          charge.chargeType === "AML Checks"
            ? {
                ...charge,
                client: amlClientNames[charge.id] || charge.client || "",
              }
            : charge,
        );
      const adviserManualPaymentEntries = paymentsForMonth(payments, selectedMonth)
        .filter(
          (payment) =>
            ["advance", "additional_payment"].includes(payment.paymentType) &&
            keyOf(payment.adviserEmail) === email,
        )
        .map(manualPaymentFeeEntry);
      const adviserSuspenseEntries = suspenseRows
        .filter((charge) => keyOf(charge.adviserEmail) === email)
        .map((charge) => ({
          id: `suspense-${charge.Index}`,
          chargeType: "Suspense",
          date: charge.Date,
          description: "Suspense charge",
          client: charge.ClientName,
          amountCharged:
            Number(String(charge.SuspenseCharge || "").replace(/[,£]/g, "")) ||
            0,
        }));
      return {
        key: `adviser:${email}`,
        entryType: "adviser",
        email,
        adviserDocumentId: listed?.adviserId || "",
        statementEmail,
        statementCc,
        ratesEditPath:
          "/advisers/" +
          encodeURIComponent(listed?.email || details?.adviserEmail || email) +
          "/rates/edit?tab=bankDetails",
        adviser: listed?.name || details?.adviser || email,
        adviserFirstName: listed?.firstName || "",
        adviserLastName: listed?.lastName || "",
        adviserFileName: personalAdviserName(listed),
        adviserDob: listed?.dob || null,
        statementPassword,
        statementDobPasswords: [
          {
            name: listed?.name || details?.adviser || email,
            dob: listed?.dob || null,
            password: statementPassword || statementPasswordFromDob(listed?.dob),
          },
        ],
        adviserLogo: listed?.logoUrl || "",
        brandName:
          listed?.brandName ||
          details?.brandName ||
          listed?.tradingStyle ||
          details?.tradingStyle ||
          listed?.name ||
          details?.adviser ||
          email,
        primaryColor:
          listed?.primaryColor ||
          details?.PrimaryColor ||
          details?.primaryColor ||
          "",
        ioAdviserIds: [
          ...new Set([
            ...(ioIdsByEmail[email] || []),
            ...(mappedIdsByAdviser.get(email) || []),
          ]),
        ],
        paymentAccount:
          [
            recipientName,
            accountHolder && accountHolder !== recipientName
              ? accountHolder
              : "",
            accountNumber ? `•••• ${String(accountNumber).slice(-4)}` : "",
          ]
            .filter(Boolean)
            .join(" · ") || "-",
        downloadFolderName:
          downloadFolderByAdviserEmail.get(email) || "No Practice",
        dateSent: "-",
        openingBalance: Number(balances.get(email) || 0),
        balance:
          Number(balances.get(email) || 0) +
          Number(currentMovements.get(email) || 0),
        feesAndCharges: buildFeesAndChargesBreakdown(
          [...adviserChargeEntries, ...adviserManualPaymentEntries],
          suspenseRows.filter((charge) => keyOf(charge.adviserEmail) === email),
        ),
        feeEntries: [
          ...adviserChargeEntries,
          ...adviserManualPaymentEntries,
          ...adviserSuspenseEntries,
        ],
      };
    });
  }, [
    advisersByEmail,
    amlClientNames,
    chargeRecords,
    groupAccounts,
    ioIdsByEmail,
    mappedIdsByAdviser,
    loadedChargeRecords,
    mappingAdvisers,
    payments,
    selectedMonth,
    suspenseRows,
  ]);

  const groupRows = useMemo(
    () =>
      Object.values(groupAccounts)
        .flatMap((group) => {
          const linkedEmails = [
            ...new Set(
              [
                ...(Array.isArray(group.adviserEmails)
                  ? group.adviserEmails
                  : []),
                ...(Array.isArray(group.adviserLinks)
                  ? group.adviserLinks.map((link) => link.adviserEmail)
                  : []),
              ]
                .map(keyOf)
                .filter(Boolean),
            ),
          ];
          const groupId = String(group.ioGroupId || group.id || "");
          const linkedRows = allAdviserRows.filter(
            (row) => linkedEmails.includes(keyOf(row.email))
              && !isExcludedGroupStatementAdviser(groupId, row),
          );
          const groupName =
            group.groupName || group.group?.GroupName || `Group ${groupId}`;
          const bankDetails = group.bankDetails || {};
          return groupStatementVariants(groupId, linkedRows, groupName).map(
            ({ id: groupVariantId, name: variantName, members }) => {
              const includedEmails = members.map((row) => keyOf(row.email));
              const statementEmails = [
                ...new Set(
                  members
                    .map((row) => String(row.statementEmail || "").trim())
                    .filter(Boolean),
                ),
              ];
              const fallbackStatementEmail = members.length === 1
                ? String(members[0].email || members[0].statementEmail || "").trim()
                : statementEmails.join(", ");
              const fallbackStatementCc = normaliseStatementCc(
                members.flatMap((row) => statementCcEmails(row.statementCc)),
              );
              const statementEmail =
                normaliseStatementCc(bankDetails.emailCommissionStatementsTo) ||
                fallbackStatementEmail;
              const statementCc =
                normaliseStatementCc(bankDetails.emailCommissionStatementsCc) ||
                fallbackStatementCc;
              const groupPassword = resolveGroupStatementPassword({
                configuredPassword: bankDetails.statementPassword,
                statementRecipient: statementEmail,
                advisers: linkedRows,
              });
              const rowKey = groupVariantId
                ? `group:${groupId}:${groupVariantId}`
                : `group:${groupId}`;
              return {
                key: rowKey,
                entryType: "group",
                email: rowKey,
                groupId,
                groupVariantId,
                statementEmail: statementEmail || "-",
                statementCc,
                adviser: variantName,
                adviserLogo: group.logoUrl || "",
                brandName: variantName,
                statementPassword: groupPassword.password,
                primaryColor:
                  group.PrimaryColor || group.primaryColor || group.primaryColour || "",
                paymentAccount:
                  [
                    variantName,
                    bankDetails.arAccountHolder &&
                    bankDetails.arAccountHolder !== variantName
                      ? bankDetails.arAccountHolder
                      : "",
                    bankDetails.arAccountNumber
                      ? `•••• ${String(bankDetails.arAccountNumber).slice(-4)}`
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" · ") || "-",
                downloadFolderName: variantName,
                dateSent: "-",
                openingBalance: members.reduce(
                  (total, row) => total + Number(row.openingBalance || 0),
                  0,
                ),
                balance: members.reduce(
                  (total, row) => total + row.balance,
                  0,
                ),
                memberCount: includedEmails.length,
                memberEmails: includedEmails,
                statementDobPasswords: groupPassword.password
                  ? [{
                      name: groupPassword.name,
                      password: groupPassword.password,
                    }]
                  : [],
                ioAdviserIds: [
                  ...new Set(members.flatMap((row) => row.ioAdviserIds || [])),
                ],
                feesAndCharges: mergeFeesAndChargesBreakdowns(
                  members.map((row) => row.feesAndCharges),
                ),
                feeEntries: members.flatMap((row) => row.feeEntries || []),
              };
            },
          );
        })
        .filter(Boolean),
    [allAdviserRows, groupAccounts],
  );

  const unmatchedQuilterRows = useMemo(() => {
    const advisersByName = new Map(
      advisers.map((adviser) => [
        keyOf(adviser.name),
        keyOf(adviser.email || adviser.id),
      ]),
    );
    const links = new Map(
      (quilterStatement?.adviserLinks || []).map((link) => [
        keyOf(link.sourceAdviserName),
        keyOf(link.email),
      ]),
    );
    const grouped = new Map();
    normaliseQuilterRows(quilterStatement?.rows || []).forEach((entry) => {
      const sourceKey = keyOf(
        entry.SourceAdviserName || entry.MappedAdviserName,
      );
      const linkedEmail =
        links.get(sourceKey) ||
        advisersByName.get(keyOf(entry.MappedAdviserName));
      if (!sourceKey || linkedEmail) return;
      const current = grouped.get(sourceKey) || {
        sourceNames: new Set(),
        adviser:
          entry.MappedAdviserName ||
          entry.SourceAdviserName ||
          "Unmatched Quilter Adviser",
        total: 0,
      };
      current.sourceNames.add(
        String(
          entry.SourceAdviserName || entry.MappedAdviserName || sourceKey,
        ).trim(),
      );
      current.total +=
        Number(String(entry.PaymentAmount || 0).replace(/[,£]/g, "")) || 0;
      grouped.set(sourceKey, current);
    });
    return [...grouped.entries()].map(([sourceKey, item]) => ({
      key: `unmatched-quilter:${sourceKey}`,
      entryType: "unmatched-quilter",
      passwordEntityId: sourceKey,
      email: "",
      statementEmail: "-",
      adviser: item.adviser,
      adviserDob: null,
      statementDobPasswords: [],
      adviserLogo: "",
      brandName: item.adviser,
      primaryColor: "",
      quilterSourceNames: [...item.sourceNames],
      ioAdviserIds: [],
      paymentAccount: "Quilter (unmatched)",
      dateSent: "-",
      openingBalance: 0,
      balance: item.total,
      feesAndCharges: buildFeesAndChargesBreakdown([]),
      feeEntries: [],
    }));
  }, [advisers, quilterStatement]);

  const unmatchedIoRows = useMemo(() => {
    const matchedRecipientIds = [
      ...Object.values(ioIdsByEmail).flat(),
      ...Object.values(ioRecipientMappings).map(
        (mapping) => mapping.recipientCrmId,
      ),
    ];
    return groupUnmatchedIoPayments(ioStatementRows, matchedRecipientIds).map(
      (item) => ({
        key: `unmatched-io:${item.recipientUserId}`,
        entryType: "unmatched-io",
        passwordEntityId: item.recipientUserId,
        email: "",
        statementEmail: "-",
        adviser: item.recipientName,
        adviserDob: null,
        statementDobPasswords: [],
        adviserLogo: "",
        brandName: item.recipientName,
        primaryColor: "",
        ioRecipientUserId: item.recipientUserId,
        ioAdviserIds: [item.recipientUserId],
        paymentAccount: `IO recipient user ID: ${item.recipientUserId}`,
        downloadFolderName: "Unmatched IO Users",
        dateSent: "-",
        openingBalance: 0,
        balance: item.total,
        paymentCount: item.paymentCount,
        feesAndCharges: buildFeesAndChargesBreakdown([]),
        feeEntries: [],
      }),
    );
  }, [ioIdsByEmail, ioRecipientMappings, ioStatementRows]);

  const ioIntroducerRows = useMemo(
    () => mappedIoIntroducerGroups(ioStatementRows, ioRecipientMappings).map(
      (item) => {
        const manualEntries = paymentsForMonth(payments, selectedMonth).filter(
          (payment) => String(payment.mappedEntityType || "").toLowerCase() === "introducer"
            && String(payment.mappedEntityId || payment.ioIntroducerId || "").trim() === item.targetId,
        );
        const openingBalance = manualEntries
          .filter((payment) => payment.paymentType === "opening_balance")
          .reduce((total, payment) => total + Number(payment.openingBalanceAmount || 0), 0);
        const manualFeeEntries = manualPaymentFeeEntries(manualEntries);
        const feesAndCharges = buildFeesAndChargesBreakdown(manualFeeEntries);
        return {
        key: `io-introducer:${item.targetId}`,
        entryType: "io-introducer",
        passwordEntityId: item.targetId,
        email: `io-introducer:${item.targetId}`,
        statementEmail: "-",
        adviser: item.name,
        adviserDob: null,
        statementDobPasswords: [],
        adviserLogo: "",
        brandName: item.name,
        primaryColor: "",
        ioRecipientUserId: item.targetId,
        ioAdviserIds: item.recipientIds,
        mappedSources: item.mappedSources,
        manualPaymentIds: manualEntries.map((payment) => payment.id).filter(Boolean),
        paymentAccount: `IO Introducer · ${item.targetId}`,
        downloadFolderName: "IO Introducers",
        dateSent: "-",
        openingBalance,
        balance: openingBalance + item.total - feesAndCharges.total,
        paymentCount: item.paymentCount,
        feesAndCharges,
        feeEntries: manualFeeEntries,
        };
      },
    ),
    [ioRecipientMappings, ioStatementRows, payments, selectedMonth],
  );

  const unknownRows = useMemo(() => {
    const grouped = new Map();
    paymentsForMonth(payments, selectedMonth)
      .filter(
        (payment) => String(payment.mappedEntityType || "").toLowerCase() === "unknown",
      )
      .forEach((payment) => {
        const unknownId = String(payment.mappedEntityId || "").trim();
        if (!unknownId) return;
        const current = grouped.get(unknownId) || {
          id: unknownId,
          name: String(payment.mappedEntityName || payment.sourceName || "Unknown").trim(),
          openingBalance: 0,
          manualFeeEntries: [],
          paymentIds: [],
          paymentCount: 0,
        };
        if (payment.paymentType === "opening_balance") {
          current.openingBalance += Number(payment.openingBalanceAmount || 0);
        }
        const feeEntry = manualPaymentFeeEntry(payment);
        if (feeEntry) current.manualFeeEntries.push(feeEntry);
        if (payment.id) current.paymentIds.push(payment.id);
        current.paymentCount += 1;
        grouped.set(unknownId, current);
      });
    return [...grouped.values()].map((item) => {
      const feesAndCharges = buildFeesAndChargesBreakdown(item.manualFeeEntries);
      return {
      key: item.id,
      entryType: "unknown",
      passwordEntityId: item.id,
      email: item.id,
      statementEmail: "-",
      adviser: item.name,
      adviserDob: null,
      statementDobPasswords: [],
      adviserLogo: "",
      brandName: item.name,
      primaryColor: "",
      unknownRecipientId: item.id,
      manualPaymentIds: item.paymentIds,
      ioAdviserIds: [],
      paymentAccount: "Unknown",
      downloadFolderName: "Unknown",
      dateSent: "-",
      openingBalance: item.openingBalance,
      balance: item.openingBalance - feesAndCharges.total,
      paymentCount: item.paymentCount,
      feesAndCharges,
      feeEntries: item.manualFeeEntries,
      };
    });
  }, [payments, selectedMonth]);

  const rows = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    const sourceRows =
      entryType === "group"
        ? groupRows
        : entryType === "unmatched-quilter"
          ? unmatchedQuilterRows
          : entryType === "io-introducer"
            ? ioIntroducerRows
          : entryType === "unknown"
            ? unknownRows
          : entryType === "unmatched-io"
            ? unmatchedIoRows
          : allAdviserRows;
    return sourceRows
      .map((row) => {
        const adviserKey = row.email || row.key;
        const statementSummary = statementSummaries.get(keyOf(adviserKey));
        const targetIds = new Set((row.ioAdviserIds || []).map(String));
        const ioEntries = [...targetIds].flatMap(
          (id) => commissionSources.ioByRecipientId.get(id) || [],
        );
        const targetEmails = new Set(
          (row.memberEmails?.length ? row.memberEmails : [row.email])
            .map(keyOf)
            .filter(Boolean),
        );
        const targetNames = new Set([keyOf(row.adviser)].filter(Boolean));
        const targetSourceNames = new Set(
          (row.quilterSourceNames || []).map(keyOf).filter(Boolean),
        );
        const quilterEntries = commissionSources.quilterRows
          .filter(({ sourceName, mappedName, linkedEmail }) =>
            row.entryType === "unmatched-quilter"
              ? targetSourceNames.has(sourceName) && !linkedEmail
              : targetEmails.has(linkedEmail) ||
                (!linkedEmail && targetNames.has(mappedName)),
          )
          .map(({ row: quilterRow }) => quilterRow);
        const finplanEntries = commissionSources.finplanRows
          .filter(({ linkedEmail, recipientName }) =>
            targetEmails.has(linkedEmail) ||
            (!linkedEmail && targetNames.has(recipientName)),
          )
          .map(({ row: finplanRow }) => finplanRow);
        const totalCommission = buildCommissionBreakdown([
          ...ioEntries,
          ...quilterEntries,
          ...finplanEntries,
        ]).total;
        const configuredStatementPassword = EXTERNAL_STATEMENT_TYPES.has(
          row.entryType,
        )
          ? statementPasswords[
              statementPasswordKey(row.entryType, row.passwordEntityId)
            ] || ""
          : row.statementPassword || "";
        const paymentSummary = buildPaymentSummary({
          openingBalance: row.openingBalance,
          totalCommission,
          feesAndCharges: row.feesAndCharges,
        });
        return {
          ...row,
          statementPassword: configuredStatementPassword,
          statementDobPasswords:
            configuredStatementPassword && EXTERNAL_STATEMENT_TYPES.has(row.entryType)
              ? [{ name: row.adviser, password: configuredStatementPassword }]
              : row.statementDobPasswords,
          balance: paymentSummary.balanceToPay,
          paymentSummary,
          archiveAdviserKey: adviserKey,
          latestVersionId: statementSummary?.latestVersionId || "",
          latestVersionNumber: Number(
            statementSummary?.latestVersionNumber || 0,
          ),
          latestStatementStatus:
            statementSummary?.latestStatus || "Not Sent",
          hasSentVersion: Boolean(statementSummary?.hasSentVersion),
          dateSent: statementSummary?.latestSentAt
            ? new Date(statementSummary.latestSentAt).toLocaleString("en-GB")
            : "-",
        };
      })
      .filter((row) =>
        `${row.adviser} ${row.statementEmail} ${row.paymentAccount}`
          .toLowerCase()
          .includes(query),
      )
      .sort((left, right) => {
        const leftValue = left[sort.key];
        const rightValue = right[sort.key];
        const comparison =
          typeof leftValue === "number"
            ? leftValue - rightValue
            : String(leftValue).localeCompare(String(rightValue), "en-GB", {
                numeric: true,
              });
        return comparison * sort.direction;
      });
  }, [
    allAdviserRows,
    commissionSources,
    deferredSearch,
    entryType,
    groupRows,
    ioIntroducerRows,
    sort,
    statementPasswords,
    statementSummaries,
    unmatchedIoRows,
    unmatchedQuilterRows,
    unknownRows,
  ]);
  const overallPaymentSummary = useMemo(
    () => rows.reduce(
      (totals, row) => ({
        openingBalance:
          totals.openingBalance + Number(row.paymentSummary?.openingBalance || 0),
        totalCommission:
          totals.totalCommission + Number(row.paymentSummary?.totalCommission || 0),
        totalCharged:
          totals.totalCharged + Number(row.paymentSummary?.totalCharged || 0),
        payments:
          totals.payments + Number(row.paymentSummary?.payments || 0),
        advances:
          totals.advances + Number(row.paymentSummary?.advances || 0),
        balanceToPay:
          totals.balanceToPay + Number(row.paymentSummary?.balanceToPay || 0),
      }),
      {
        openingBalance: 0,
        totalCommission: 0,
        totalCharged: 0,
        payments: 0,
        advances: 0,
        balanceToPay: 0,
      },
    ),
    [rows],
  );
  const commissionSummaryLoading =
    unmatchedIoLoading || quilterLoading || finplanLoading ||
    ioIdsLoading || ioMappingsLoading;
  const eligibleRows = useMemo(
    () => rows.filter(isStatementSendEligible),
    [rows],
  );
  const selectedStatementCount = useMemo(
    () => eligibleRows.filter((row) => selected.has(row.key)).length,
    [eligibleRows, selected],
  );
  const selectedPreviouslySentCount = useMemo(
    () => eligibleRows.filter(
      (row) => selected.has(row.key) && row.latestStatementStatus === "Sent",
    ).length,
    [eligibleRows, selected],
  );
  const statementMonthLabel = useMemo(
    () => formatMonthKey(selectedMonth),
    [selectedMonth],
  );
  const statementDownloadMonthLabel = useMemo(() => {
    const match = String(selectedMonth || "").match(/^(\d{4})-(\d{2})$/);
    if (!match) return statementMonthLabel;
    return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
      .toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      });
  }, [selectedMonth, statementMonthLabel]);
  useEffect(() => {
    if (!previewRow) return;
    const updatedRow = rows.find((row) => row.key === previewRow.key);
    if (!updatedRow) {
      setPreviewRow(null);
      return;
    }
    if (updatedRow !== previewRow) {
      setPreviewRow(updatedRow);
    }
  }, [previewRow, rows]);
  useEffect(() => {
    const eligibleKeys = new Set(eligibleRows.map((row) => row.key));
    setSelected((current) => {
      const next = new Set(
        [...current].filter((key) => eligibleKeys.has(key)),
      );
      return next.size === current.size ? current : next;
    });
  }, [eligibleRows]);
  const allSelected =
    eligibleRows.length > 0 &&
    eligibleRows.every((row) => selected.has(row.key));
  const toggleAll = () =>
    setSelected(
      allSelected ? new Set() : new Set(eligibleRows.map((row) => row.key)),
    );
  const toggleRow = (row) => {
    if (!isStatementSendEligible(row)) return;
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(row.key)) next.delete(row.key);
      else next.add(row.key);
      return next;
    });
  };
  const sortBy = (key) =>
    setSort((current) => ({
      key,
      direction: current.key === key ? -current.direction : 1,
    }));
  const changeEntryType = (nextEntryType) => {
    setEntryType(nextEntryType);
    setSelected(new Set());
    setPreviewRow(null);
  };
  const openRecipientMapping = (event, row) => {
    const anchor = event.currentTarget.getBoundingClientRect();
    const popupWidth = 380;
    const preferredLeft = anchor.left >= popupWidth + 20
      ? anchor.left - popupWidth - 8
      : anchor.right + 8;
    setMappingPosition({
      left: Math.max(
        12,
        Math.min(preferredLeft, window.innerWidth - popupWidth - 12),
      ),
      top: Math.max(12, Math.min(anchor.top, window.innerHeight - 360)),
    });
    setMappingRow(row);
    setMappingValue("");
    setMappingError("");
  };
  const closeRecipientMapping = () => {
    if (mappingSaving) return;
    setMappingRow(null);
    setMappingPosition(null);
    setMappingValue("");
    setMappingError("");
  };
  const confirmRecipientMapping = async () => {
    const adviserOnly = ["io-introducer", "unmatched-quilter"].includes(
      mappingRow?.entryType,
    );
    const option = recipientMappingOptions.find((item) => item.id === mappingValue);
    if (!mappingRow || !option?.target) {
      setMappingError(adviserOnly
        ? "Select an adviser."
        : "Select an adviser or IO introducer.");
      return;
    }
    setMappingSaving(true);
    setMappingError("");
    try {
      if (mappingRow.entryType === "unmatched-quilter") {
        await saveQuilterAdviserLinks(
          selectedMonth,
          linkQuilterSourcesToAdviser({
            adviserLinks: quilterStatement?.adviserLinks || [],
            sourceNames:
              mappingRow.quilterSourceNames || [mappingRow.adviser],
            adviser: option.target,
          }),
        );
      } else if (mappingRow.entryType === "unknown") {
        await saveManualPaymentRecipientMapping({
          paymentIds: mappingRow.manualPaymentIds,
          sourceName: mappingRow.adviser,
          target: option.target,
        });
      } else if (mappingRow.entryType === "io-introducer") {
        await saveIORecipientMappings({
          recipientCrmIds: mappingRow.ioAdviserIds?.length
            ? mappingRow.ioAdviserIds
            : [mappingRow.ioRecipientUserId],
          sourceName: mappingRow.adviser,
          target: option.target,
        });
        if (mappingRow.manualPaymentIds?.length) {
          await saveManualPaymentRecipientMapping({
            paymentIds: mappingRow.manualPaymentIds,
            sourceName: mappingRow.adviser,
            target: option.target,
          });
        }
      } else {
        await saveIORecipientMapping({
          recipientCrmId: mappingRow.ioRecipientUserId,
          sourceName: mappingRow.adviser,
          target: option.target,
        });
      }
      setMappingRow(null);
      setMappingPosition(null);
      setMappingValue("");
    } catch (error) {
      setMappingError(error.message || "The IO recipient mapping could not be saved.");
    } finally {
      setMappingSaving(false);
    }
  };
  const separateMappedRecipient = async (mappedSource) => {
    if (!mappedSource?.recipientCrmId) return;
    setMappingSaving(true);
    setMappingError("");
    try {
      await separateIORecipientMapping(mappedSource);
      setMappingRow((current) => current ? {
        ...current,
        ioAdviserIds: (current.ioAdviserIds || []).filter(
          (id) => String(id) !== String(mappedSource.recipientCrmId),
        ),
        mappedSources: (current.mappedSources || []).filter(
          (source) => source.recipientCrmId !== mappedSource.recipientCrmId,
        ),
      } : current);
    } catch (error) {
      setMappingError(error.message || "The IO recipient could not be separated.");
    } finally {
      setMappingSaving(false);
    }
  };
  const openStatementPassword = (row) => {
    setPasswordRow(row);
    setPasswordValue(row.statementPassword || "");
    setPasswordError("");
  };
  const closeStatementPassword = () => {
    if (passwordSaving) return;
    setPasswordRow(null);
    setPasswordValue("");
    setPasswordError("");
  };
  const confirmStatementPassword = async () => {
    const password = String(passwordValue || "").replace(/\s+/g, "");
    if (!passwordRow?.passwordEntityId) {
      setPasswordError("The statement record could not be identified.");
      return;
    }
    if (!password) {
      setPasswordError("Enter a statement password.");
      return;
    }
    setPasswordSaving(true);
    setPasswordError("");
    try {
      await saveStatementPassword({
        entryType: passwordRow.entryType,
        entityId: passwordRow.passwordEntityId,
        displayName: passwordRow.adviser,
        statementPassword: password,
      });
      setPasswordRow(null);
      setPasswordValue("");
    } catch (error) {
      setPasswordError(error.message || "The statement password could not be saved.");
    } finally {
      setPasswordSaving(false);
    }
  };
  const statementFileName = (row, extension = "pdf") =>
    commissionStatementFileName({
      adviser: row.adviser,
      firstName: row.adviserFirstName,
      lastName: row.adviserLastName,
      monthLabel: statementDownloadMonthLabel,
      extension,
    });
  const statementIdentifier = (row) => queueValue({
    entryType: row.entryType,
    adviserDocumentId: row.adviserDocumentId || "",
    adviserEmail: row.email || "",
    groupId: row.entryType === "group"
      ? row.groupId || String(row.email || "").replace(/^group:/, "")
      : "",
    groupVariantId: row.entryType === "group"
      ? row.groupVariantId || ""
      : "",
    displayName: row.adviser || "",
    adviserFileName: row.adviserFileName || "",
    downloadFolderName: row.downloadFolderName || "",
    quilterSourceNames: row.quilterSourceNames || [],
    ioRecipientUserId: row.ioRecipientUserId || "",
    ioAdviserIds: row.ioAdviserIds || [],
    unknownRecipientId: row.unknownRecipientId || "",
    passwordEntityId: row.passwordEntityId || "",
    statementEmail: row.statementEmail === "-" ? "" : row.statementEmail || "",
    statementCc: row.statementCc || "",
  });
  const freshStatementFile = (row, fileType) =>
    generateFreshMonthlyStatementFile({
      incomePeriodId: statementIncomePeriodId,
      adviserKey: row.archiveAdviserKey || row.email || row.key,
      monthKey: selectedMonth,
      identifier: statementIdentifier(row),
      fileType,
    });
  const storeFreshStatement = async (
    row,
    {
      refresh = true,
    } = {},
  ) => {
    const version = await generateFreshMonthlyStatementVersion({
      incomePeriodId: statementIncomePeriodId,
      adviserKey: row.archiveAdviserKey || row.email || row.key,
      monthKey: selectedMonth,
      identifier: statementIdentifier(row),
    });
    if (refresh) {
      historySubscriptionsRef.current.get(row.key)?.();
      historySubscriptionsRef.current.delete(row.key);
      setHistoryVersionsByKey((current) => {
        const next = { ...current };
        delete next[row.key];
        return next;
      });
      setExpandedHistoryKey("");
      await refreshStatementSummaries();
    }
    return { version };
  };
  const storedFile = (row, versionId, fileType) =>
    getStoredMonthlyStatementFile({
      incomePeriodId: statementIncomePeriodId,
      adviserKey: row.archiveAdviserKey || row.email || row.key,
      versionId,
      fileType,
    });
  const downloadStoredFile = async (row, version, fileType) => {
    setDownloading(true);
    setDownloadError("");
    try {
      const blob = await storedFile(row, version.id, fileType);
      const fileName = statementFileName(row, fileType === "pdf" ? "pdf" : "xlsx");
      saveAs(blob, fileName);
    } catch {
      setDownloadError("The statement file could not be downloaded.");
    } finally {
      setDownloading(false);
    }
  };

  const triggerCloudArchiveDownload = (downloadUrl) => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const startStatementArchiveDownload = async (statementRows, options = {}) => {
    if (!statementRows.length) return;
    downloadJobSubscriptionRef.current?.();
    downloadJobSubscriptionRef.current = null;
    downloadedJobIdRef.current = "";
    setDownloading(true);
    setDownloadError("");
    setDownloadProgress({
      stage: "Preparing",
      total: statementRows.length,
      processed: 0,
      filesPrepared: 0,
    });
    try {
      const requestId = window.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const entries = statementRows.map((row) => {
        const useStored = options.forceStored
          || (row.entryType !== "group"
            && options.useLatestStored
            && row.latestStatementStatus === "Sent"
            && row.latestVersionId);
        return {
          adviserKey: row.archiveAdviserKey || row.email || row.key,
          versionId: useStored ? row.latestVersionId || options.versionId || "" : "",
          useStored: Boolean(useStored),
          identifier: statementIdentifier(row),
        };
      });
      const job = await createMonthlyStatementDownloadArchive({
        incomePeriodId: statementIncomePeriodId,
        requestId,
        monthKey: selectedMonth,
        monthLabel: statementDownloadMonthLabel,
        entries,
      });
      const jobId = job.id || job.jobId;
      let unsubscribe = () => {};
      unsubscribe = subscribeMonthlyStatementDownloadArchive(
        jobId,
        (current) => {
          if (!current) return;
          const stage = ["Queued", "Processing"].includes(current.status)
            ? "Preparing"
            : current.status || "Preparing";
          setDownloadProgress({
            stage,
            total: Number(current.total || statementRows.length),
            processed: Number(current.processed || 0),
            filesPrepared: Number(current.filesPrepared || 0),
          });
          if (stage === "Ready") {
            if (
              current.downloadUrl
              && downloadedJobIdRef.current !== jobId
            ) {
              downloadedJobIdRef.current = jobId;
              triggerCloudArchiveDownload(current.downloadUrl);
            }
            setDownloading(false);
            setDownloadProgress(null);
            unsubscribe();
            if (downloadJobSubscriptionRef.current === unsubscribe) {
              downloadJobSubscriptionRef.current = null;
            }
          } else if (stage === "Failed") {
            setDownloadError(
              current.error || "The statement ZIP could not be prepared.",
            );
            setDownloading(false);
            unsubscribe();
            if (downloadJobSubscriptionRef.current === unsubscribe) {
              downloadJobSubscriptionRef.current = null;
            }
          }
        },
        () => {
          setDownloadProgress((current) => ({
            ...(current || {}),
            stage: "Failed",
          }));
          setDownloadError("The statement ZIP status could not be loaded.");
          setDownloading(false);
        },
      );
      downloadJobSubscriptionRef.current = unsubscribe;
    } catch (error) {
      setDownloadProgress((current) => ({
        ...(current || {}),
        stage: "Failed",
      }));
      setDownloadError(
        error.message || "The statement ZIP could not be prepared.",
      );
      setDownloading(false);
    }
  };

  const downloadStatement = async (
    row,
    versionId = row.latestVersionId,
    forceStored = false,
  ) => {
    const useStored = Boolean(
      forceStored
      || (row.entryType !== "group"
        && versionId
        && row.latestStatementStatus === "Sent"),
    );
    await startStatementArchiveDownload(
      [{ ...row, latestVersionId: useStored ? versionId : "" }],
      { forceStored: useStored, versionId },
    );
  };
  const downloadSelectedStatements = async () => {
    const selectedRows = rows.filter(
      (row) => selected.has(row.key) && isStatementSendEligible(row),
    );
    if (!selectedRows.length) return;
    const selectedAdviserEmails = new Set(
      selectedRows
        .filter((row) => row.entryType === "adviser")
        .map((row) => keyOf(row.email)),
    );
    const affectedGroups = groupRows.filter(
      (group) => group.memberCount > 1
        && group.memberEmails.some((email) => selectedAdviserEmails.has(keyOf(email))),
    );
    const statementRows = [...new Map(
      [...selectedRows, ...affectedGroups].map((row) => [row.key, row]),
    ).values()];
    await startStatementArchiveDownload(statementRows);
  };
  const openStatement = async (
    row,
    versionId = row.latestVersionId,
    forceStored = false,
  ) => {
    setPreviewLoading(true);
    setPreviewRow(null);
    setStoredPreviewUrl("");
    try {
      if (
        (forceStored
          || (row.entryType !== "group" && row.latestStatementStatus === "Sent"))
        && versionId
      ) {
        setStoredPreviewUrl(URL.createObjectURL(await storedFile(row, versionId, "pdf")));
        setPreviewRow(row);
      } else {
        setStoredPreviewUrl(URL.createObjectURL(await freshStatementFile(row, "pdf")));
        setPreviewRow(row);
      }
    } catch {
      // Technical errors are not rendered in the UI.
    } finally {
      setPreviewLoading(false);
    }
  };
  const regenerateStatement = async (row) => {
    setDownloading(true);
    try {
      await storeFreshStatement(row);
    } catch {
      // Failed versions remain in server-side history.
    } finally {
      setDownloading(false);
    }
  };
  const sendSelectedStatements = async () => {
    const selectedRows = rows.filter(
      (row) => selected.has(row.key) && isStatementSendEligible(row),
    );
    if (!selectedRows.length) return;
    const requestId = window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setSendingStatements(true);
    setSendJobItems([]);
    setSendProgress({
      stage: "Queueing",
      total: selectedRows.length,
      processed: 0,
      sent: 0,
      failed: 0,
    });
    try {
      const entries = selectedRows.map((row) => ({
        adviserKey: row.archiveAdviserKey || row.email || row.key,
        versionId: row.entryType === "group" || row.latestStatementStatus === "Sent"
          ? ""
          : row.latestVersionId || "",
        identifier: statementIdentifier(row),
      }));
      const job = await createMonthlyStatementSendBatch({
        incomePeriodId: statementIncomePeriodId,
        requestId,
        monthKey: selectedMonth,
        monthLabel: statementMonthLabel,
        sendMode: "manual",
        entries,
      });
      const jobId = job.id || job.jobId;
      sendJobSubscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
      sendJobSubscriptionsRef.current = [
        subscribeMonthlyStatementSendBatch(
          jobId,
          (current) => {
            if (!current) return;
            const nextProgress = {
              stage: current.status || "Queued",
              total: Number(current.total || selectedRows.length),
              processed: Number(current.processed || 0),
              sent: Number(current.sent || 0),
              failed: Number(current.failed || 0),
            };
            const everythingSent = current.status === "Completed" &&
              nextProgress.total > 0 &&
              nextProgress.sent >= nextProgress.total &&
              nextProgress.failed === 0;
            setSendProgress(everythingSent ? null : nextProgress);
            if (everythingSent) setSendJobItems([]);
            if (["Completed", "Completed With Errors", "Failed"].includes(current.status)) {
              setSendingStatements(false);
              refreshStatementSummaries();
            }
          },
          () => {},
        ),
        subscribeMonthlyStatementSendBatchItems(
          jobId,
          setSendJobItems,
          () => {},
        ),
      ];
      setSendProgress({
        stage: job.status || "Queued",
        total: Number(job.total || selectedRows.length),
        processed: Number(job.processed || 0),
        sent: Number(job.sent || 0),
        failed: Number(job.failed || 0),
      });
    } catch {
      setSendingStatements(false);
      setSendProgress(null);
    }
  };
  const confirmSendSelectedStatements = () => {
    setSendConfirmationOpen(false);
    void sendSelectedStatements();
  };
  const toggleStatementHistory = async (row) => {
    if (expandedHistoryKey === row.key) {
      historySubscriptionsRef.current.get(row.key)?.();
      historySubscriptionsRef.current.delete(row.key);
      setExpandedHistoryKey("");
      return;
    }
    historySubscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
    historySubscriptionsRef.current.clear();
    setExpandedHistoryKey(row.key);
    const adviserKey = row.archiveAdviserKey || row.email || row.key;
    const loadHistoryFromApi = async () => {
      try {
        const result = await getMonthlyStatementHistory(
          statementIncomePeriodId,
          adviserKey,
        );
        setHistoryVersionsByKey((current) => ({
          ...current,
          [row.key]: result.versions || [],
        }));
      } catch {
        setHistoryVersionsByKey((current) => ({ ...current, [row.key]: [] }));
      }
    };
    const adviserDocumentId = statementSummaries.get(keyOf(adviserKey))?.id;
    if (!adviserDocumentId) {
      await loadHistoryFromApi();
      return;
    }
    try {
      const unsubscribe = subscribeMonthlyStatementHistory(
        statementIncomePeriodId,
        adviserDocumentId,
        (versions) => setHistoryVersionsByKey((current) => ({
          ...current,
          [row.key]: versions,
        })),
        () => {
          historySubscriptionsRef.current.delete(row.key);
          loadHistoryFromApi();
        },
      );
      historySubscriptionsRef.current.set(row.key, unsubscribe);
    } catch {
      await loadHistoryFromApi();
    }
  };

  return (
    <section
      aria-label="Commission statements"
      data-month={selectedMonth}
      className="fit-column gap-s"
    >
      <header className="row space-between gap-l">
        <h3 className="FP_H3">Adviser Commission Statements</h3>
      </header>
      {previewLoading ? (
        <div
          className="monthly-payments-page-loading"
          role="status"
          aria-live="polite"
        >
          <span className="monthly-payments-spinner" aria-hidden="true" />
          <span className="FP_Content">Loading commission statement...</span>
        </div>
      ) : null}
      {previewRow ? (
        <div
          className="commission-pdf-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${previewRow.adviser} commission statement`}
        >
          <div className="commission-pdf-modal-panel">
            <IconButton
              className="commission-pdf-modal-close"
              onClick={() => setPreviewRow(null)}
              label="Close commission statement"
              icon={<CloseIcon aria-hidden="true" />}
            />
            <iframe className="commission-stored-pdf" src={storedPreviewUrl} title={previewRow.adviser + " commission statement"} />
          </div>
        </div>
      ) : null}
      <Popup
        open={sendConfirmationOpen}
        title="Send statements?"
        description={(
          <>
            <strong>{selectedStatementCount}</strong>{" "}
            {selectedStatementCount === 1 ? "statement" : "statements"} will be sent.
            {selectedPreviouslySentCount ? (
              <> {selectedPreviouslySentCount} previously sent {selectedPreviouslySentCount === 1 ? "statement" : "statements"} will be regenerated from the latest data and sent as a new version.</>
            ) : null}
          </>
        )}
        icon={<SendMailIcon />}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={confirmSendSelectedStatements}
        onCancel={() => setSendConfirmationOpen(false)}
      />
      <Popup
        open={Boolean(passwordRow)}
        title={`Statement password for ${passwordRow?.adviser || ""}`}
        description={(
          <div className="fit-column gap-s">
            <p>
              Set the password used to protect this statement PDF. Spaces are
              removed automatically.
            </p>
            <Field
              label="Statement Password"
              type="password"
              value={passwordValue}
              onChange={(value) => {
                setPasswordValue(String(value || "").replace(/\s+/g, ""));
                setPasswordError("");
              }}
              showPasswordToggle
              full
              disabled={passwordSaving}
            />
            {passwordError ? (
              <p className="FP_Help_Text text-error" role="alert">
                {passwordError}
              </p>
            ) : null}
          </div>
        )}
        icon={<PasswordKeyIcon />}
        confirmLabel="Save password"
        cancelLabel="Cancel"
        busy={passwordSaving}
        onConfirm={confirmStatementPassword}
        onCancel={closeStatementPassword}
      />
      {mappingRow && mappingPosition
        ? createPortal(
            <div
              className="opening-balance-mapping-popover commission-recipient-mapping-popover"
              style={mappingPosition}
              role="dialog"
              aria-label={["io-introducer", "unmatched-quilter"].includes(
                mappingRow.entryType,
              )
                ? `Link ${mappingRow.adviser} to an adviser`
                : `Map ${mappingRow.adviser} to an adviser or IO introducer`}
              onClick={(event) => event.stopPropagation()}
            >
              <IconButton
                className="opening-balance-mapping-close"
                icon={<CloseIcon aria-hidden="true" />}
                label="Close recipient mapping"
                onClick={closeRecipientMapping}
              />
              <div className="commission-recipient-mapping-form">
                <p className="FP_Content">
                  {["io-introducer", "unmatched-quilter"].includes(
                    mappingRow.entryType,
                  )
                    ? `Choose the adviser who should receive all payments for ${mappingRow.adviser}.`
                    : (
                      <>
                        Choose the adviser or IO introducer that should receive payments{" "}
                        {mappingRow.entryType === "unknown"
                          ? `for ${mappingRow.adviser}.`
                          : `for IO recipient ID ${mappingRow.ioRecipientUserId || "-"}.`}
                      </>
                    )}
                </p>
                <CustomSelect
                  label="Map to"
                  options={recipientMappingOptions}
                  value={mappingValue}
                  placeholder={mappingRow.entryType === "unmatched-quilter"
                    ? "Select adviser email"
                    : mappingRow.entryType === "io-introducer"
                      ? "Select adviser"
                      : "Select adviser or IO introducer"}
                  searchPlaceholder={["io-introducer", "unmatched-quilter"].includes(
                    mappingRow.entryType,
                  )
                    ? "Search advisers or emails"
                    : "Search advisers or IO introducers"}
                  clearable={false}
                  onChange={(event) => {
                    setMappingValue(event.target.value);
                    setMappingError("");
                  }}
                />
                {mappingRow.entryType === "io-introducer" &&
                mappingRow.mappedSources?.length ? (
                  <div className="commission-recipient-mapping-sources">
                    <p className="FP_Help_Text_Semibold">Grouped recipients</p>
                    <p className="FP_Help_Text">
                      Separate a recipient to show it as its own IO Introducer row.
                    </p>
                    {mappingRow.mappedSources.map((source) => (
                      <div
                        className="commission-recipient-mapping-source"
                        key={source.recipientCrmId}
                      >
                        <span className="FP_Help_Text">
                          {source.sourceName} · {source.recipientCrmId}
                        </span>
                        <ButtonTextLink
                          label="Separate"
                          disabled={mappingSaving}
                          onClick={() => separateMappedRecipient(source)}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
                {mappingError ? (
                  <p className="FP_Help_Text text-error" role="alert">
                    {mappingError}
                  </p>
                ) : null}
              </div>
              <div className="opening-balance-mapping-actions">
                <PrimaryButton
                  label={mappingSaving ? "SAVING..." : "SAVE MAPPING"}
                  disabled={mappingSaving || !mappingValue}
                  onClick={confirmRecipientMapping}
                />
              </div>
            </div>,
            document.body,
          )
        : null}
      <div className="commission-statements-controls gap-l">
        <div role="group" aria-label="Commission Statements month">
          <RadioGroup
            name="commission-summary-month"
            options={monthOptions}
            value={selectedMonth}
            onChange={(event) => onMonthChange(event.target.value)}
            inline
          />
        </div>
        <div className="commission-statements-toolbar gap-m">
          <div className="commission-entry-type-filter">
            <CustomSelect
              label="View"
              options={ENTRY_TYPE_OPTIONS}
              value={entryType}
              clearable={false}
              searchable={false}
              onChange={(event) => changeEntryType(event.target.value)}
            />
          </div>
          <SearchField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search"
          />
          <IconButton
            icon={<DownloadIcon aria-hidden="true" />}
            label="Download selected commission statements"
            loadingLabel={`${downloadProgress?.stage || "Preparing"} commission statement ZIP`}
            loading={downloading}
            disabled={!selectedStatementCount || downloading || sendingStatements}
            onClick={downloadSelectedStatements}
          />
          {/* <IconButton
            icon={<SendMailIcon aria-hidden="true" />}
            label="Send selected commission statements"
            loadingLabel="Preparing and sending selected commission statements"
            loading={sendingStatements}
            disabled={!selectedStatementCount || downloading || sendingStatements}
            onClick={() => setSendConfirmationOpen(true)}
          /> */}
        </div>
      </div>
      {downloadProgress ? (
        <div className="commission-send-progress" role="status" aria-live="polite">
          <div className="row align-center gap-s FP_Help_Text">
            <strong className="FP_Help_Text_Semibold primary">
              {downloadProgress.stage}
            </strong>
            <span>
              {downloadProgress.processed} of {downloadProgress.total} statements prepared
              {downloadProgress.filesPrepared
                ? ` · ${downloadProgress.filesPrepared} files`
                : ""}
            </span>
          </div>
          {downloadError ? (
            <p className="FP_Help_Text text-error" role="alert">
              {downloadError}
            </p>
          ) : null}
        </div>
      ) : null}
      {sendProgress ? (
        <div className="commission-send-progress" role="status" aria-live="polite">
          <div className="row align-center gap-s FP_Help_Text">
            <strong className="FP_Help_Text_Semibold primary">{sendProgress.stage}</strong>
            <span>
              {sendProgress.processed} of {sendProgress.total} processed
              {` · ${sendProgress.sent} sent · ${sendProgress.failed} failed`}
            </span>
          </div>
          {sendJobItems.length ? (
            <div className="commission-send-items" aria-label="Statement send status">
              {sendJobItems
                .slice()
                .sort((left, right) => String(left.payload?.adviser || left.adviserKey).localeCompare(String(right.payload?.adviser || right.adviserKey)))
                .map((item) => (
                  <div className="commission-send-item FP_Help_Text" key={item.id}>
                    <span>{item.payload?.adviser || item.adviserKey}</span>
                    <strong className={`commission-send-item-status status-${String(item.status || "queued").toLowerCase().replace(/\s+/g, "-")}`}>
                      {item.status || "Queued"}
                    </strong>
                  </div>
                ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <section className="website-dashboard-card opening-balances-card commission-statements-card">
        <div className="website-table-wrap table-scroll-container">
          <table
            className="website-table commission-statements-table"
            aria-label="Adviser commission statements"
          >
            <thead>
              <tr>
                <th className="commission-select-column">
                  <input
                    className="commission-statement-checkbox"
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={!eligibleRows.length}
                    aria-label="Select all eligible statements"
                    title={eligibleRows.length
                      ? "Select all eligible statements"
                      : "No statements are currently eligible to send"}
                  />
                </th>
                {[
                  [
                    "adviser",
                    entryType === "group"
                      ? "Group"
                      : entryType === "io-introducer"
                        ? "IO Introducer"
                      : entryType === "unknown"
                        ? "Unknown"
                      : entryType === "unmatched-io"
                        ? "IO User"
                        : "Adviser",
                  ],
                  [
                    "email",
                    "Details",
                  ],
                  ["balance", "Payment Summary"],
                ].map(([key, label]) => (
                  <th
                    key={key}
                    className={`FP_Help_Text_Semibold uppercase commission-${key}-column ${key === "balance" ? "text-align-right" : ""}`}
                  >
                    <button
                      type="button"
                      className={`table-heading-content table-sort-button ${key === "balance" ? "justify-flex-end" : ""}`}
                      onClick={() => sortBy(key)}
                    >
                      {label} <SortIcon aria-hidden="true" />
                    </button>
                  </th>
                ))}
                <th className="FP_Help_Text_Semibold uppercase commission-actions-column">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingAdvisers ||
              loadingUnappointedAdvisers ||
              chargesLoading ||
              commissionSummaryLoading ||
              (entryType === "unmatched-io" &&
                ioIntroducersLoading) ||
              (entryType === "io-introducer" &&
                ioIntroducersLoading) ? (
                <tr>
                  <td colSpan="5" className="FP_Content">
                    Loading commission statements...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row) => {
                  const statementActions = statementActionAvailability(row);
                  const sendEligible = statementActions.canSend;
                  const hasNegativeClosingBalance = statementClosingBalance(row) < 0;
                  const hasMissingPassword = hasMissingStatementPassword(row);
                  return (
                  <Fragment key={row.key}>
                  <tr className={[
                    expandedHistoryKey === row.key ? "commission-statement-row-open" : "",
                    hasMissingPassword
                      ? "commission-statement-password-warning"
                      : hasNegativeClosingBalance
                        ? "commission-statement-negative"
                        : "",
                  ].filter(Boolean).join(" ")}>
                    <td className="commission-select-column">
                      <input
                        className="commission-statement-checkbox"
                        type="checkbox"
                        checked={sendEligible && selected.has(row.key)}
                        onChange={() => toggleRow(row)}
                        disabled={!sendEligible}
                        aria-label={`Select ${row.adviser}`}
                      />
                    </td>
                    <td className="FP_Help_Text_Semibold commission-adviser-column">
                      <div className="fit-column gap-xs">
                        <div className="commission-adviser-name-row">
                          <span>
                            {row.adviser}
                            {row.entryType === "group"
                              ? ` (${row.memberCount} ${row.memberCount === 1 ? "adviser" : "advisers"})`
                              : ""}
                          </span>
                          {hasMissingPassword ? (
                            <WarningIcon
                              className="commission-password-warning-icon"
                              role="img"
                              aria-label="Statement password unavailable"
                              title="Statement password unavailable"
                            />
                          ) : null}
                        </div>
                        {row.entryType === "adviser" && row.email ? (
                          <span className="FP_Help_Text commission-adviser-email">
                            {row.email}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="FP_Help_Text commission-email-column commission-details-column">
                      <div className="commission-details">
                        <div className="commission-details-row">
                          <span className="commission-details-label">
                            {["io-introducer", "unmatched-io", "unknown", "unmatched-quilter"].includes(row.entryType)
                              ? "Mapped To"
                              : "Email to Send Statement"}
                          </span>
                          {["io-introducer", "unmatched-io", "unknown", "unmatched-quilter"].includes(row.entryType) ? (
                            <div className="row align-center gap-s">
                              <IconButton
                                icon={<EditIcon aria-hidden="true" />}
                                label={["io-introducer", "unmatched-quilter"].includes(row.entryType)
                                  ? `Link ${row.adviser} to an adviser`
                                  : `Map ${row.adviser} to an adviser or IO introducer`}
                                onClick={(event) => openRecipientMapping(event, row)}
                              />
                              <span>{row.entryType === "unmatched-quilter"
                                ? "Select adviser email"
                                : row.entryType === "io-introducer"
                                  ? "Select adviser"
                                  : "Select adviser or IO introducer"}</span>
                            </div>
                          ) : row.ratesEditPath ? (
                            <Link className="table-url-link" to={row.ratesEditPath}>
                              {row.statementEmail}
                            </Link>
                          ) : (
                            <span>{row.statementEmail}</span>
                          )}
                        </div>
                        {row.statementCc ? (
                          <div className="commission-details-row">
                            <span className="commission-details-label">CC</span>
                            <span>{row.statementCc}</span>
                          </div>
                        ) : null}
                        <div className="commission-details-row">
                          <span className="commission-details-label">Payment Account</span>
                          <span>{row.paymentAccount}</span>
                        </div>
                        {EXTERNAL_STATEMENT_TYPES.has(row.entryType) ? (
                          <div className="commission-details-row">
                            <span className="commission-details-label">
                              Statement Password
                            </span>
                            <div className="row align-center gap-s">
                              <IconButton
                                icon={<PasswordKeyIcon aria-hidden="true" />}
                                label={`${row.statementPassword ? "Edit" : "Add"} ${row.adviser} statement password`}
                                onClick={() => openStatementPassword(row)}
                              />
                              <span>
                                {row.statementPassword ? "Saved" : "Add password"}
                              </span>
                            </div>
                          </div>
                        ) : null}
                        <div className="commission-details-row">
                          <span className="commission-details-label">Date Sent</span>
                          <span>{row.dateSent}</span>
                        </div>
                      </div>
                    </td>
                    <td
                      className="FP_Help_Text commission-amount-column"
                    >
                      <PaymentSummaryCell summary={row.paymentSummary} />
                    </td>
                    <td className="commission-actions-column">
                      <div className="commission-row-actions row align-center gap-xs width-full">
                        <div className="row align-center gap-xs">
                          <IconButton
                            icon={<ViewIcon aria-hidden="true" />}
                            onClick={() => openStatement(row)}
                            label={`View ${row.adviser} statement`}
                            disabled={!statementActions.canView}
                          />
                          <IconButton
                            icon={<DownloadIcon aria-hidden="true" />}
                            onClick={() => downloadStatement(row)}
                            label={`Download ${row.adviser} statement`}
                            loading={downloading}
                            disabled={!statementActions.canDownload}
                          />
                          {row.hasSentVersion ? (
                            <IconButton
                              icon={<RegenerateIcon aria-hidden="true" />}
                              onClick={() => regenerateStatement(row)}
                              label={"Regenerate " + row.adviser + " statement"}
                              loading={downloading}
                            />
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className={`table-row-action commission-history-toggle ${expandedHistoryKey === row.key ? "is-expanded" : ""}`}
                          aria-expanded={expandedHistoryKey === row.key}
                          aria-label={`${expandedHistoryKey === row.key ? "Hide" : "Show"} ${row.adviser} statement history`}
                          onClick={() => toggleStatementHistory(row)}
                        >
                          <ArrowDownIcon aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedHistoryKey === row.key ? (
                    <tr className="commission-history-expanded-row">
                      <td colSpan="5">
                        <section className="commission-history-cards" aria-label={`${row.adviser} statement history`}>
                          {(historyVersionsByKey[row.key] || []).map((version) => {
                            const versionHistoryKey = `${row.key}:${version.id}`;
                            const isVersionExpanded = !collapsedHistoryVersions.has(versionHistoryKey);
                            return (
                            <article className="commission-history-card" key={version.id}>
                              <header className="commission-history-card-header">
                                <div className="row align-center gap-s">
                                  <span className="commission-version-badge FP_Help_Text_Semibold">Version {version.versionNumber}</span>
                                  {version.id === row.latestVersionId ? <span className="commission-latest-badge FP_Help_Text_Semibold">Latest</span> : null}
                                  <span className={`commission-status-badge FP_Help_Text_Semibold commission-status-${String(version.status || "not-sent").toLowerCase().replace(/\s+/g, "-")}`}>
                                    {statementStatusIcon(version.status)}
                                    {version.status || "Not Sent"}
                                  </span>
                                </div>
                                <div className="row gap-xs">
                                  <IconButton icon={<ViewIcon aria-hidden="true" />} label={`View version ${version.versionNumber}`} onClick={() => openStatement(row, version.id, true)} />
                                  <IconButton icon={<DownloadIcon aria-hidden="true" />} label={`Download version ${version.versionNumber}`} onClick={() => downloadStatement(row, version.id, true)} />
                                  <button
                                    type="button"
                                    className={`table-row-action commission-version-toggle ${isVersionExpanded ? "is-expanded" : ""}`}
                                    aria-expanded={isVersionExpanded}
                                    aria-label={`${isVersionExpanded ? "Hide" : "Show"} version ${version.versionNumber} details`}
                                    onClick={() => setCollapsedHistoryVersions((current) => {
                                      const next = new Set(current);
                                      if (next.has(versionHistoryKey)) next.delete(versionHistoryKey);
                                      else next.add(versionHistoryKey);
                                      return next;
                                    })}
                                  >
                                    <ArrowDownIcon aria-hidden="true" />
                                  </button>
                                </div>
                              </header>
                              {isVersionExpanded ? (
                              <div className="commission-history-card-grid">
                                <div className="commission-history-detail">
                                  <div className="commission-history-detail-group">
                                    <span className="FP_Help_Text_Semibold">Adviser</span>
                                    <strong className="FP_Help_Text_Semibold primary">{version.adviserName || row.adviser}</strong>
                                  </div>
                                  <div className="commission-history-detail-group">
                                    <span className="FP_Help_Text_Semibold">Recipient</span>
                                    <strong className="FP_Help_Text_Semibold primary">{version.recipient || "-"}</strong>
                                  </div>
                                </div>
                                <div className="commission-history-detail">
                                  <div className="commission-history-detail-group">
                                    <span className="FP_Help_Text_Semibold">Income period</span>
                                    <strong className="FP_Help_Text_Semibold primary">{version.month || statementMonthLabel}</strong>
                                  </div>
                                  <div className="commission-history-detail-group">
                                    <span className="FP_Help_Text_Semibold">Generated</span>
                                    <strong className="FP_Help_Text_Semibold primary">{version.generatedAt ? new Date(version.generatedAt).toLocaleString("en-GB") : "-"}</strong>
                                  </div>
                                </div>
                                <div className="commission-history-detail">
                                  <div className="commission-history-detail-group">
                                    <span className="FP_Help_Text_Semibold">Email status</span>
                                    <strong className="commission-detail-status FP_Help_Text_Semibold primary">{statementStatusIcon(version.emailDeliveryStatus)}{version.emailDeliveryStatus || "Not Sent"}</strong>
                                  </div>
                                  <div className="commission-history-detail-group">
                                    <span className="FP_Help_Text_Semibold">Sent</span>
                                    <strong className="FP_Help_Text_Semibold primary">{version.sentAt ? new Date(version.sentAt).toLocaleString("en-GB") : "-"}</strong>
                                  </div>
                                </div>
                                <div className="commission-history-detail commission-history-files-detail">
                                  <span className="FP_Help_Text_Semibold">Stored files</span>
                                  <div className="commission-statement-files">
                                    {version.pdfStored ? (
                                      <button type="button" className="table-url-link FP_Help_Text" onClick={() => openStatement(row, version.id, true)}>
                                        {version.pdfFileName || "PDF statement"}
                                      </button>
                                    ) : <strong>PDF not stored</strong>}
                                    {version.excelStored ? (
                                      <button type="button" className="table-url-link FP_Help_Text" onClick={() => downloadStoredFile(row, version, "excel")}>
                                        {version.excelFileName || "Excel statement"}
                                      </button>
                                    ) : <strong>Excel not stored</strong>}
                                  </div>
                                </div>
                                <div className="commission-history-detail commission-statement-passwords">
                                  <span className="FP_Help_Text_Semibold">PDF passwords</span>
                                  {version.pdfPasswordProtected ? (
                                    <div className="FP_Help_Text">
                                      <div>Fixed: <strong>chequers_road</strong></div>
                                      {(row.statementDobPasswords || []).map((entry) => (
                                        <div key={`${entry.name}-${entry.password || "missing"}`}>Password: <strong>{entry.password || "Unavailable"}</strong></div>
                                      ))}
                                    </div>
                                  ) : <strong className="FP_Help_Text_Semibold primary">Not password-protected</strong>}
                                </div>
                                {/* <div className="commission-history-detail commission-history-error-detail">
                                  <span className="FP_Help_Text_Semibold">Errors</span><strong className="FP_Help_Text_Semibold primary">{version.error || "None"}</strong>
                                </div> */}
                              </div>
                              ) : null}
                            </article>
                            );
                          })}
                          {!historyVersionsByKey[row.key] ? (
                            <div className="commission-history-empty FP_Help_Text">Loading statement history...</div>
                          ) : !(historyVersionsByKey[row.key] || []).length ? (
                            <div className="commission-history-empty FP_Help_Text">No stored statement history.</div>
                          ) : null}
                        </section>
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="FP_Content">
                    No{" "}
                    {entryType === "group"
                      ? "group"
                      : entryType === "unmatched-quilter"
                        ? "unmatched Quilter adviser"
                      : entryType === "unmatched-io"
                          ? "unmatched IO user"
                        : entryType === "io-introducer"
                          ? "IO introducer"
                        : entryType === "unknown"
                          ? "unknown recipient"
                        : "adviser"}{" "}
                    commission statements match your search.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" className="FP_Help_Text_Semibold">
                  Overall Payment Summary
                </td>
                <td className="FP_Help_Text_Semibold commission-amount-column">
                  <PaymentSummaryCell summary={overallPaymentSummary} />
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </section>
  );
};

export default withMonthlyPaymentsAccess(CommissionStatements);
